import React from "react";
import { Box, Typography, Paper } from "@mui/material";
import dayjs from "dayjs";

const DAY_HEADERS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Builds a continuous run of real calendar days from the first week-start through
// 6 days after the last week-start (i.e. the full final week), chunked into true
// Mon–Sun rows. Because the semester happens to start on a Monday and the last
// survey week ends on a Sunday, this divides evenly with no partial rows.
function buildCalendarRows(allWeeks) {
  const firstDay = dayjs(allWeeks[0]);
  const lastDay = dayjs(allWeeks[allWeeks.length - 1]).add(6, "day");

  const days = [];
  let cursor = firstDay;
  while (!cursor.isAfter(lastDay)) {
    days.push(cursor);
    cursor = cursor.add(1, "day");
  }

  const rows = [];
  for (let i = 0; i < days.length; i += 7) {
    rows.push(days.slice(i, i + 7));
  }
  return rows;
}

// Each real calendar row corresponds to exactly one WEEK_STARTS entry — normally the
// row's own Monday, but for the Labor Day week the entry (9/8, a Tuesday) falls a
// day into the row instead. This finds whichever entry belongs to a given row.
function weekKeyForRow(row, allWeeks) {
  const rowStart = row[0];
  const rowEnd = row[6];
  return allWeeks.find((w) => {
    const wd = dayjs(w);
    return !wd.isBefore(rowStart, "day") && !wd.isAfter(rowEnd, "day");
  });
}

// Mirrors services/surveySchedule.js buildCustomSchedule()'s index-based stepping, for display
// only. The backend remains the source of truth at deploy time — this is purely so the
// calendar highlights the weeks that will actually get a survey, not every week in the
// selected range. Stepping by WEEK_STARTS array index (not raw date math) matters here:
// if the chosen start is the Labor Day week (9/8, a Tuesday), adding "2 weeks" as calendar
// time would drift onto Tuesdays forever instead of landing back on the real listed weeks.
function computeUnlockWeeks(startISO, endISO, allWeeks) {
  if (!startISO || !endISO) return [];
  const startIdx = allWeeks.indexOf(startISO);
  const endIdx = allWeeks.indexOf(endISO);
  if (startIdx === -1 || endIdx === -1 || endIdx < startIdx) return [];

  const isOnTime = startIdx === 0;
  const weeks = [];

  if (isOnTime) {
    weeks.push(allWeeks[0]);
    let idx = 1;
    while (idx <= endIdx) {
      weeks.push(allWeeks[idx]);
      idx += 2;
    }
  } else {
    let idx = startIdx;
    while (idx <= endIdx) {
      weeks.push(allWeeks[idx]);
      idx += 2;
    }
  }
  return weeks;
}

/**
 * Calendar-style week picker, rendered as a real day grid (Mon–Sun) — every date
 * sits in its true weekday column; clicking any day in a row selects that row's
 * survey week.
 *
 * Props:
 * - allWeeks: string[] — every week-start date to render (full semester list)
 * - availableWeeks: string[] — the subset that's currently clickable
 * - startWeek, endWeek: string | "" — currently selected values
 * - stage: "start" | "end"
 * - onSelectWeek: (iso: string) => void
 */
export default function SurveyScheduleCalendar({
  allWeeks,
  availableWeeks,
  startWeek,
  endWeek,
  stage,
  onSelectWeek,
}) {
  const rows = buildCalendarRows(allWeeks);
  const unlockWeeks = computeUnlockWeeks(startWeek, endWeek, allWeeks);

  const isSelectable = (weekKey) => weekKey && availableWeeks.includes(weekKey);

  let currentMonthLabel = null;

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
        {stage === "start"
          ? "Select a start week."
          : "Now select a cutoff week — the latest week a survey can unlock."}
      </Typography>

      {/* Day-of-week header, shown once */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5, mb: 0.5 }}>
        {DAY_HEADERS.map((d) => (
          <Typography key={d} variant="caption" align="center" color="text.secondary" sx={{ fontWeight: 600 }}>
            {d}
          </Typography>
        ))}
      </Box>

      {rows.map((row) => {
        const weekKey = weekKeyForRow(row, allWeeks);
        const rowMonthLabel = row[0].format("MMMM YYYY");
        const showMonthHeader = rowMonthLabel !== currentMonthLabel;
        currentMonthLabel = rowMonthLabel;

        const selectable = isSelectable(weekKey);
        const isStart = weekKey === startWeek;
        const isEnd = weekKey === endWeek;
        const isBoundary = isStart || isEnd;
        const isUnlockWeek = weekKey && unlockWeeks.includes(weekKey);

        let bgcolor = "background.paper";
        let color = "text.primary";

        if (isUnlockWeek) {
          bgcolor = "primary.light";
          color = "primary.contrastText";
        } else if (!selectable) {
          bgcolor = "action.disabledBackground";
          color = "text.disabled";
        }

        return (
          <React.Fragment key={row[0].format("YYYY-MM-DD")}>
            {showMonthHeader && (
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5, fontWeight: 600 }}>
                {rowMonthLabel}
              </Typography>
            )}
            <Paper
              variant="outlined"
              onClick={() => selectable && onSelectWeek(weekKey)}
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: 0.5,
                p: 0.5,
                mb: 0.5,
                cursor: selectable ? "pointer" : "not-allowed",
                bgcolor,
                border: isBoundary ? "3px solid" : "1px solid",
                borderColor: isBoundary ? "primary.dark" : "divider",
                transition: "background-color 0.15s ease",
                "&:hover": selectable
                  ? { bgcolor: isUnlockWeek ? bgcolor : "action.hover" }
                  : {},
              }}
            >
              {row.map((d) => {
                const showMonth = d.date() === 1;
                return (
                  <Box key={d.format("YYYY-MM-DD")} sx={{ textAlign: "center" }}>
                    <Typography
                      variant="caption"
                      sx={{ color, fontWeight: isBoundary ? 700 : 400 }}
                    >
                      {showMonth ? d.format("MMM D") : d.format("D")}
                    </Typography>
                  </Box>
                );
              })}
            </Paper>
          </React.Fragment>
        );
      })}

      <Box sx={{ display: "flex", gap: 2, mt: 1, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ width: 14, height: 14, border: "3px solid", borderColor: "primary.dark", borderRadius: 0.5 }} />
          <Typography variant="caption" color="text.secondary">Start / Cutoff</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ width: 14, height: 14, bgcolor: "primary.light", borderRadius: 0.5 }} />
          <Typography variant="caption" color="text.secondary">Survey week</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box sx={{ width: 14, height: 14, bgcolor: "action.disabledBackground", borderRadius: 0.5 }} />
          <Typography variant="caption" color="text.secondary">Unavailable</Typography>
        </Box>
      </Box>
    </Box>
  );
}

// import React from "react";
// import { Box, Typography } from "@mui/material";
// import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
// import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
// import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
// import { PickerDay } from "@mui/x-date-pickers/PickerDay";
// import dayjs from "dayjs";

// // Finds which WEEK_STARTS entry "owns" a given day — i.e. the entry whose
// // [start, start+6] range contains it. Handles the Labor Day week (9/8, a Tuesday)
// // correctly since it's a real range check, not an assumption that weeks start Monday.
// function weekKeyForDate(day, allWeeks) {
//   if (!day) return undefined;
//   return allWeeks.find((w) => {
//     const start = dayjs(w);
//     const end = start.add(6, "day");
//     return !day.isBefore(start, "day") && !day.isAfter(end, "day");
//   });
// }

// // Mirrors services/surveySchedule.js buildCustomSchedule()'s index-based stepping, for
// // display only. Stepping by WEEK_STARTS array index (not raw date math) matters because
// // if the chosen start is the Labor Day week (a Tuesday), adding "2 weeks" as calendar time
// // would drift onto Tuesdays forever instead of landing back on the real listed weeks.
// function computeUnlockWeeks(startISO, endISO, allWeeks) {
//   if (!startISO || !endISO) return [];
//   const startIdx = allWeeks.indexOf(startISO);
//   const endIdx = allWeeks.indexOf(endISO);
//   if (startIdx === -1 || endIdx === -1 || endIdx < startIdx) return [];

//   const isOnTime = startIdx === 0;
//   const weeks = [];

//   if (isOnTime) {
//     weeks.push(allWeeks[0]);
//     let idx = 1;
//     while (idx <= endIdx) {
//       weeks.push(allWeeks[idx]);
//       idx += 2;
//     }
//   } else {
//     let idx = startIdx;
//     while (idx <= endIdx) {
//       weeks.push(allWeeks[idx]);
//       idx += 2;
//     }
//   }
//   return weeks;
// }

// /**
//  * Calendar-style week picker built on @mui/x-date-pickers' DateCalendar.
//  * Every day sits in its correct weekday column (handled by the library); clicking
//  * any day selects the survey week that day belongs to.
//  *
//  * Props:
//  * - allWeeks: string[] — every week-start date to render (full semester list)
//  * - availableWeeks: string[] — the subset that's currently clickable
//  * - startWeek, endWeek: string | "" — currently selected values
//  * - stage: "start" | "end"
//  * - onSelectWeek: (iso: string) => void
//  */
// export default function SurveyScheduleCalendar({
//   allWeeks,
//   availableWeeks,
//   startWeek,
//   endWeek,
//   stage,
//   onSelectWeek,
// }) {
//   const unlockWeeks = computeUnlockWeeks(startWeek, endWeek, allWeeks);
//   const minDate = dayjs(allWeeks[0]);
//   const maxDate = dayjs(allWeeks[allWeeks.length - 1]).add(6, "day");

//   const isSelectable = (weekKey) => Boolean(weekKey) && availableWeeks.includes(weekKey);

//   const CustomDay = (dayProps) => {
//     const { day, outsideCurrentMonth, ...other } = dayProps;
//     const weekKey = weekKeyForDate(day, allWeeks);
//     const selectable = isSelectable(weekKey);
//     const isStart = weekKey === startWeek;
//     const isEnd = weekKey === endWeek;
//     const isBoundary = isStart || isEnd;
//     const isUnlockWeek = weekKey && unlockWeeks.includes(weekKey);

//     let bgcolor = undefined;
//     let color = undefined;
//     if (isUnlockWeek) {
//       bgcolor = "primary.light";
//       color = "primary.contrastText";
//     } else if (!selectable) {
//       color = "text.disabled";
//     }

//     return (
//       <PickerDay
//         {...other}
//         day={day}
//         outsideCurrentMonth={outsideCurrentMonth}
//         disabled={!selectable}
//         onDaySelect={() => selectable && onSelectWeek(weekKey)}
//         sx={{
//           bgcolor,
//           color,
//           border: isBoundary ? "3px solid" : "1px solid transparent",
//           borderColor: isBoundary ? "primary.dark" : "transparent",
//           fontWeight: isBoundary ? 700 : 400,
//           "&:hover": {
//             bgcolor: selectable ? (isUnlockWeek ? "primary.light" : "action.hover") : undefined,
//           },
//           "&.Mui-disabled": {
//             color: "text.disabled",
//           },
//         }}
//       />
//     );
//   };

//   return (
//     <Box>
//       <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
//         {stage === "start"
//           ? "Select a start week."
//           : "Now select a cutoff week — the latest week a survey can unlock. Surveys unlock every two weeks starting from your start week and stop once the next one would fall after this cutoff, so the last survey may unlock a bit earlier than the week you pick here."}
//       </Typography>

//       <LocalizationProvider dateAdapter={AdapterDayjs}>
//         <DateCalendar
//           value={null}
//           minDate={minDate}
//           maxDate={maxDate}
//           shouldDisableDate={(day) => !isSelectable(weekKeyForDate(day, allWeeks))}
//           slots={{ day: CustomDay }}
//         />
//       </LocalizationProvider>

//       <Box sx={{ display: "flex", gap: 2, mt: 1, flexWrap: "wrap" }}>
//         <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
//           <Box sx={{ width: 14, height: 14, border: "3px solid", borderColor: "primary.dark", borderRadius: 0.5 }} />
//           <Typography variant="caption" color="text.secondary">Start / Cutoff</Typography>
//         </Box>
//         <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
//           <Box sx={{ width: 14, height: 14, bgcolor: "primary.light", borderRadius: 0.5 }} />
//           <Typography variant="caption" color="text.secondary">Survey week</Typography>
//         </Box>
//         <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
//           <Box sx={{ width: 14, height: 14, bgcolor: "action.disabledBackground", borderRadius: 0.5 }} />
//           <Typography variant="caption" color="text.secondary">Unavailable</Typography>
//         </Box>
//       </Box>
//     </Box>
//   );
// }